/**
 * ScheduleDeliveryModal — create a delivery for a saved report (#183).
 *
 * Two triggers: a clock **cadence** (daily/weekly/monthly digest) or **when a
 * test configuration completes**. Recipients are org members. On save, POSTs a
 * `report_subscriptions` doc; scheduled subs fire from the hourly cron,
 * on-completion subs fire from the `test_runs` terminal-state trigger. Both
 * email an inline summary + signed CSV/PDF links.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/Select';
import { Button, Spinner } from '@/components/ui';
import { Label } from '@/components/ui/Label';
import { Switch } from '@/components/ui/Switch';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { useToast } from '@/contexts/ToastContext';
import { getTestConfigurations } from '@/services/testConfigurationService';
import { createReportSubscription } from '@/services/reportSubscriptionService';
import type {
  ReportDeliveryCadence,
  ReportDeliveryWindow,
  ReportSubscriptionTrigger,
  ReportSubscriptionInput,
} from '@simcheck/shared-types';

interface Props {
  open: boolean;
  onClose: () => void;
  organizationId: string;
  savedReportId: string;
  savedReportName: string;
  onCreated?: () => void;
}

const DOW = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const WINDOW_OPTIONS: Array<{ value: ReportDeliveryWindow; label: string }> = [
  { value: 'last_24h', label: 'Last 24 hours' },
  { value: 'last_7d', label: 'Last 7 days' },
  { value: 'last_30d', label: 'Last 30 days' },
  { value: 'last_month', label: 'Last calendar month' },
];

const browserTz = (() => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch { return 'UTC'; }
})();

function defaultWindowFor(freq: ReportDeliveryCadence['frequency']): ReportDeliveryWindow {
  return freq === 'daily' ? 'last_24h' : freq === 'monthly' ? 'last_month' : 'last_7d';
}

export default function ScheduleDeliveryModal({
  open, onClose, organizationId, savedReportId, savedReportName, onCreated,
}: Props) {
  const toast = useToast();
  const [members, setMembers] = useState<Array<{ userId: string; email: string; displayName: string | null }>>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [configs, setConfigs] = useState<Array<{ id: string; name: string }>>([]);
  const [loadingConfigs, setLoadingConfigs] = useState(false);
  const [saving, setSaving] = useState(false);

  const [trigger, setTrigger] = useState<ReportSubscriptionTrigger>('scheduled');

  // Scheduled cadence
  const [frequency, setFrequency] = useState<ReportDeliveryCadence['frequency']>('weekly');
  const [dayOfWeek, setDayOfWeek] = useState(1); // Monday
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [hour, setHour] = useState(8);
  const [minute, setMinute] = useState(0);

  // On-completion
  const [testConfigurationId, setTestConfigurationId] = useState<string>('');
  const [deliverOn, setDeliverOn] = useState<'always' | 'on_failure'>('always');

  const [windowSel, setWindowSel] = useState<ReportDeliveryWindow>('last_7d');
  const [windowTouched, setWindowTouched] = useState(false);
  const [csv, setCsv] = useState(true);
  const [pdf, setPdf] = useState(true);
  const [recipients, setRecipients] = useState<string[]>([]);

  useEffect(() => {
    if (!open) return;
    setLoadingMembers(true);
    // Read the canonical membership subcollection (the /organizations/:id/members
    // REST endpoint's array-contains query never matches real membership objects).
    getDocs(collection(db, 'organizations', organizationId, 'members'))
      .then((snap) => setMembers(snap.docs.map((d) => ({
        userId: d.id,
        email: (d.data().email as string) || '',
        displayName: (d.data().displayName as string) || null,
      }))))
      .catch(() => toast.error('Failed to load org members'))
      .finally(() => setLoadingMembers(false));
  }, [open, organizationId, toast]);

  // Lazy-load test configurations the first time the on-completion trigger is picked.
  useEffect(() => {
    if (!open || trigger !== 'on_completion' || configs.length > 0 || loadingConfigs) return;
    setLoadingConfigs(true);
    getTestConfigurations(organizationId)
      .then((list) => setConfigs(list.map((c) => ({ id: c.id, name: c.name }))))
      .catch(() => toast.error('Failed to load test configurations'))
      .finally(() => setLoadingConfigs(false));
  }, [open, trigger, organizationId, configs.length, loadingConfigs, toast]);

  // Window auto-tracks the frequency (scheduled) until the user overrides it.
  useEffect(() => {
    if (trigger === 'scheduled' && !windowTouched) setWindowSel(defaultWindowFor(frequency));
    if (trigger === 'on_completion' && !windowTouched) setWindowSel('last_24h');
  }, [trigger, frequency, windowTouched]);

  const timeLabel = useMemo(
    () => `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`,
    [hour, minute],
  );

  const toggleRecipient = (uid: string) =>
    setRecipients((prev) => (prev.includes(uid) ? prev.filter((u) => u !== uid) : [...prev, uid]));

  const canSave =
    recipients.length > 0 && (csv || pdf) && !saving &&
    (trigger === 'scheduled' || !!testConfigurationId);

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const input: ReportSubscriptionInput = {
        savedReportId,
        trigger,
        format: { csv, pdf },
        recipients,
        window: windowSel,
        enabled: true,
      };
      if (trigger === 'scheduled') {
        input.cadence = {
          frequency, hour, minute, timezone: browserTz,
          ...(frequency === 'weekly' ? { dayOfWeek } : {}),
          ...(frequency === 'monthly' ? { dayOfMonth } : {}),
        };
      } else {
        input.testConfigurationId = testConfigurationId;
        input.deliverOn = deliverOn;
      }
      await createReportSubscription(organizationId, input);
      toast.success('Delivery created');
      onCreated?.();
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to create delivery');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Schedule delivery</DialogTitle>
          <DialogDescription>
            Email “{savedReportName}” to org members, with an inline summary and CSV/PDF download links.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto">
          {/* Trigger */}
          <div>
            <Label className="text-xs">Send</Label>
            <Select value={trigger} onValueChange={(v) => { setTrigger(v as ReportSubscriptionTrigger); setWindowTouched(false); }}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="scheduled">On a schedule</SelectItem>
                <SelectItem value="on_completion">When a test completes</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {trigger === 'scheduled' ? (
            <>
              {/* Cadence */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Frequency</Label>
                  <Select value={frequency} onValueChange={(v) => setFrequency(v as ReportDeliveryCadence['frequency'])}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {frequency === 'weekly' && (
                  <div>
                    <Label className="text-xs">Day of week</Label>
                    <Select value={String(dayOfWeek)} onValueChange={(v) => setDayOfWeek(Number(v))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {DOW.map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {frequency === 'monthly' && (
                  <div>
                    <Label className="text-xs">Day of month</Label>
                    <Select value={String(dayOfMonth)} onValueChange={(v) => setDayOfMonth(Number(v))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                          <SelectItem key={d} value={String(d)}>{d}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              {/* Time + window */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Time ({timeLabel})</Label>
                  <div className="flex gap-2">
                    <Select value={String(hour)} onValueChange={(v) => setHour(Number(v))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 24 }, (_, i) => i).map((h) => (
                          <SelectItem key={h} value={String(h)}>{String(h).padStart(2, '0')}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={String(minute)} onValueChange={(v) => setMinute(Number(v))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {[0, 15, 30, 45].map((m) => (
                          <SelectItem key={m} value={String(m)}>{String(m).padStart(2, '0')}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-1">Timezone: {browserTz}</p>
                </div>
                <div>
                  <Label className="text-xs">Covering</Label>
                  <Select value={windowSel} onValueChange={(v) => { setWindowSel(v as ReportDeliveryWindow); setWindowTouched(true); }}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {WINDOW_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Test configuration + deliverOn + window */}
              <div>
                <Label className="text-xs">Test configuration</Label>
                {loadingConfigs ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground py-2"><Spinner className="w-4 h-4" /> Loading test configurations…</div>
                ) : configs.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-2">No saved test configurations in this org.</p>
                ) : (
                  <Select value={testConfigurationId} onValueChange={setTestConfigurationId}>
                    <SelectTrigger><SelectValue placeholder="Select a test…" /></SelectTrigger>
                    <SelectContent>
                      {configs.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Send when</Label>
                  <Select value={deliverOn} onValueChange={(v) => setDeliverOn(v as 'always' | 'on_failure')}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="always">Every run</SelectItem>
                      <SelectItem value="on_failure">Only on failure</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Covering</Label>
                  <Select value={windowSel} onValueChange={(v) => { setWindowSel(v as ReportDeliveryWindow); setWindowTouched(true); }}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {WINDOW_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </>
          )}

          {/* Formats */}
          <div>
            <Label className="text-xs">Attach download links</Label>
            <div className="flex items-center gap-6 mt-1">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Switch checked={csv} onCheckedChange={setCsv} /> CSV
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Switch checked={pdf} onCheckedChange={setPdf} /> PDF
              </label>
            </div>
          </div>

          {/* Recipients */}
          <div>
            <Label className="text-xs">Recipients (org members)</Label>
            {loadingMembers ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-2"><Spinner className="w-4 h-4" /> Loading members…</div>
            ) : members.length === 0 ? (
              <p className="text-sm text-muted-foreground py-2">No members found.</p>
            ) : (
              <div className="mt-1 max-h-40 overflow-y-auto rounded-md border border-border divide-y divide-border">
                {members.map((m) => (
                  <label key={m.userId} className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-muted/50">
                    <input
                      type="checkbox"
                      className="accent-primary"
                      checked={recipients.includes(m.userId)}
                      onChange={() => toggleRecipient(m.userId)}
                    />
                    <span className="flex-1 truncate">{m.displayName || m.email}</span>
                    <span className="text-xs text-muted-foreground truncate">{m.email}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={!canSave} icon={saving ? <Spinner className="w-4 h-4" /> : undefined}>
            Create delivery
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
